import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const priorityLabel = (priorite) => {
  switch (priorite?.toLowerCase()) {
    case 'critique':
      return { 
        text: "Critique", 
        style: {
          backgroundColor: '#ffebee',
          color: '#c62828'
        }
      };
    case 'haute':
      return { 
        text: "Haute", 
        style: {
          backgroundColor: '#fff3e0',
          color: '#f57c00'
        }
      };
    case 'moyenne':
      return { 
        text: "Moyenne", 
        style: {
          backgroundColor: '#fff8e1',
          color: '#ff8f00'
        }
      };
    case 'basse':
      return { 
        text: "Basse", 
        style: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32'
        }
      };
    default:
      return { 
        text: "Moyenne", 
        style: {
          backgroundColor: '#fff8e1',
          color: '#ff8f00'
        }
      };
  }
};

// Bug corrigé : ce switch ne matchait jamais 'terminee' (la vraie valeur
// renvoyée par le backend, sans accent ni "é") ni 'annulee' du tout — une
// tâche terminée ou annulée retombait silencieusement sur "En attente".
const statusLabel = (statut) => {
  switch (statut?.toLowerCase()) {
    case 'en_cours':
    case 'en cours':
      return {
        text: "En cours",
        style: {
          backgroundColor: '#e3f2fd',
          color: '#1565c0'
        }
      };
    case 'en_validation':
    case 'en validation':
      return {
        text: "En attente de validation",
        style: {
          backgroundColor: '#fff3e0',
          color: '#ef6c00'
        }
      };
    case 'terminee':
    case 'termine':
    case 'terminé':
    case 'terminée':
      return {
        text: "Terminée",
        style: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32'
        }
      };
    case 'annulee':
    case 'annule':
    case 'annulé':
    case 'annulée':
      return {
        text: "Annulée",
        style: {
          backgroundColor: '#ffebee',
          color: '#c62828'
        }
      };
    case 'en_attente':
    case 'en attente':
    default:
      return {
        text: "En attente",
        style: {
          backgroundColor: '#f5f5f5',
          color: '#616161'
        }
      };
  }
};

const formatDate = (dateString) => {
  if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateTimeFull = (dateString) => {
  if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const TacheDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tache, setTache] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    }
  }, [navigate]);

  const fetchTacheDetail = async () => {
    if (!id || isNaN(parseInt(id))) {
      setError("ID de tâche invalide");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/taches/get.php?id=${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data?.success) {
        setTache(response.data.tache);
      } else {
        setError(response.data?.message || "Tâche non trouvée.");
      }
    } catch (err) {
      console.error("Erreur lors du chargement:", err);
      
      let errorMsg = "Erreur lors du chargement des détails.";
      
      if (err.response) {
        if (err.response.data && err.response.data.message) {
          errorMsg = err.response.data.message;
        } else {
          switch (err.response.status) {
            case 400:
              errorMsg = "ID de tâche invalide.";
              break;
            case 401:
              errorMsg = "Session expirée. Veuillez vous reconnecter.";
              localStorage.removeItem("access_token");
              setTimeout(() => navigate("/login"), 2000);
              break;
            case 403:
              errorMsg = "Accès interdit. Vous n'avez pas les permissions nécessaires.";
              break;
            case 404:
              errorMsg = "Tâche non trouvée.";
              break;
            case 500:
              errorMsg = "Erreur serveur. Veuillez contacter l'administrateur.";
              break;
            default:
              errorMsg = `Erreur ${err.response.status}: ${err.response.data?.message || 'Erreur serveur'}`;
          }
        }
      } else if (err.request) {
        errorMsg = "Serveur inaccessible. Vérifiez votre connexion.";
      } else {
        errorMsg = "Erreur lors de la requête: " + err.message;
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTacheDetail();
  }, [id]);

  // Valider (clôturer) ou renvoyer en cours une tâche en attente de
  // validation — seul le gestionnaire peut le faire (voir la nouvelle règle
  // de transition dans taches/change_status.php : l'agent envoie en
  // validation, il ne peut plus clôturer seul).
  const handleChangeStatutTache = async (nouveauStatut) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        alert("Session expirée. Veuillez vous reconnecter.");
        navigate("/login");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/taches/change_status.php`,
        { id_tache: tache.id_tache, nouveau_statut: nouveauStatut },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data?.success) {
        setTache({ ...tache, statut_tache: nouveauStatut });
      } else {
        alert(response.data?.message || "Impossible de changer le statut.");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors du changement de statut.");
    }
  };

  const handleDeleteTache = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible.")) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        alert("Session expirée. Veuillez vous reconnecter.");
        navigate("/login");
        return;
      }

      const response = await axios.delete(
        `${API_BASE_URL}/taches/delete.php`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: { id_tache: tache.id_tache }
        }
      );

      if (response.data?.success) {
        alert("Tâche supprimée avec succès !");
        navigate("/taches");
      } else {
        alert(response.data?.message || "Erreur lors de la suppression.");
      }
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      alert("Erreur lors de la suppression de la tâche.");
    }
  };

  const getAgentName = () => {
    if (tache.agent_info?.nom_complet) return tache.agent_info.nom_complet;
    return tache.agent_nom && tache.agent_prenom 
      ? `${tache.agent_prenom} ${tache.agent_nom}`
      : tache.agent_nom || 'Agent inconnu';
  };

  const getAgentInitials = () => {
    const name = getAgentName();
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const isLate = () => {
    if (!tache.date_fin || ['terminee', 'termine', 'terminé', 'terminée', 'annulee'].includes(tache.statut_tache)) {
      return false;
    }
    const now = new Date();
    const dueDate = new Date(tache.date_fin);
    return dueDate < now;
  };

  const calculateDuration = () => {
    if (!tache.date_debut || !tache.date_fin) return null;
    
    const start = new Date(tache.date_debut);
    const end = new Date(tache.date_fin);
    const diffMs = end - start;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    
    let durationText = '';
    if (diffDays > 0) {
      durationText += `${diffDays} jour${diffDays > 1 ? 's' : ''} `;
    }
    if (remainingHours > 0) {
      durationText += `${remainingHours} heure${remainingHours > 1 ? 's' : ''}`;
    }
    
    return durationText || 'Moins d\'une heure';
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de la tâche</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/taches" style={{ color: COLORS.primary }}>Tâches</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des détails...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de la tâche</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/taches" style={{ color: COLORS.primary }}>Tâches</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Erreur</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">
                        <h6 className="mb-1" style={{ color: '#c62828' }}>Erreur</h6>
                        <p className="mb-0">{error}</p>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                  <div className="text-center">
                    <button 
                      className="btn fw-semibold"
                      onClick={() => navigate("/taches")}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i> Retour aux tâches
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!tache) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de la tâche</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/taches" style={{ color: COLORS.primary }}>Tâches</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#fff3e0',
                    borderColor: COLORS.warning,
                    color: '#f57c00',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.warning}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">
                        <h6 className="mb-1" style={{ color: '#f57c00' }}>Aucune donnée</h6>
                        <p className="mb-0">Impossible de charger les détails de la tâche.</p>
                      </div>
                    </div>
                  </div>
                  <button 
                    className="btn fw-semibold"
                    onClick={() => navigate("/taches")}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i> Retour aux tâches
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const priorite = priorityLabel(tache.priorite);
  const statut = statusLabel(tache.statut_tache);
  const isTaskLate = isLate();
  const duration = calculateDuration();

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de la tâche</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/taches" style={{ color: COLORS.primary }}>Tâches</Link>
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                {/* En-tête simplifiée avec seulement le bouton Retour */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      borderBottom: `2px solid ${COLORS.lightBg}`,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-card-checklist"></i>
                        </div>
                        Détails de la tâche
                        {userInfo && userInfo.profile === 3 && (
                          <span className="badge fw-semibold ms-3" style={{ 
                            backgroundColor: '#E8F5E9',
                            color: '#2E7D32',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem'
                          }}>
                            <i className="bi bi-shield-check me-1"></i>
                            Gestionnaire
                          </span>
                        )}
                      </div>
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                      <i className="bi bi-info-circle me-1"></i>
                      Visualisez tous les détails de cette tâche
                    </p>
                  </div>
                  
                  {/* SEULEMENT le bouton Retour en haut */}
                  <button
                    className="btn fw-semibold"
                    onClick={() => navigate("/taches")}
                    title="Retour à la liste des tâches"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#E3F2FD';
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i> Retour
                  </button>
                </div>

                <div className="row">
                  {/* Informations principales */}
                  <div className="col-lg-6">
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-header" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `1px solid ${COLORS.lightBg}`,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        padding: '16px'
                      }}>
                        <h6 className="card-title mb-0" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-info-circle"></i>
                          </div>
                          Informations de la tâche
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-md-12 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-card-heading me-1" style={{ color: COLORS.secondary }}></i>
                              Titre
                            </label>
                            <h5 style={{ color: COLORS.primary }}>
                              {tache.titre_tache}
                            </h5>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-exclamation-triangle me-1" style={{ color: COLORS.secondary }}></i>
                              Priorité
                            </label>
                            <p>
                              <span className="badge fw-semibold" style={{ 
                                ...priorite.style,
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontSize: '0.85rem'
                              }}>
                                {priorite.text}
                              </span>
                            </p>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-funnel me-1" style={{ color: COLORS.secondary }}></i>
                              Statut
                            </label>
                            <p>
                              <span className="badge fw-semibold" style={{ 
                                ...statut.style,
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontSize: '0.85rem'
                              }}>
                                {statut.text}
                                {isTaskLate && (
                                  <i className="bi bi-exclamation-triangle ms-1"></i>
                                )}
                              </span>
                              {isTaskLate && (
                                <small className="text-danger d-block mt-1">
                                  <i className="bi bi-clock-history me-1"></i>
                                  Tâche en retard
                                </small>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                            <i className="bi bi-card-text me-1" style={{ color: COLORS.secondary }}></i>
                            Description
                          </label>
                          <div className="border rounded p-3" style={{ 
                            borderColor: '#e0e0e0',
                            backgroundColor: '#F8F9FA',
                            color: COLORS.darkText,
                            minHeight: '100px'
                          }}>
                            {tache.description_tache || 'Aucune description fournie.'}
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
                              Date de début
                            </label>
                            <p style={{ color: COLORS.darkText }}>
                              {formatDate(tache.date_debut)}
                            </p>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-calendar-minus me-1" style={{ color: COLORS.secondary }}></i>
                              Date de fin
                            </label>
                            <p style={{ 
                              color: isTaskLate ? COLORS.danger : COLORS.darkText,
                              fontWeight: isTaskLate ? '600' : 'normal'
                            }}>
                              {formatDate(tache.date_fin)}
                              {isTaskLate && (
                                <i className="bi bi-exclamation-triangle ms-1"></i>
                              )}
                            </p>
                          </div>
                          
                          {duration && (
                            <div className="col-md-12 mb-3">
                              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                                <i className="bi bi-clock me-1" style={{ color: COLORS.secondary }}></i>
                                Durée estimée
                              </label>
                              <p style={{ color: COLORS.darkText }}>
                                {duration}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Commentaire final et preuve */}
                    {(tache.commentaire_final || tache.preuve_path) && (
                      <div className="card mb-4" style={{ 
                        borderRadius: '8px',
                        border: `1px solid ${COLORS.lightBg}`,
                        boxShadow: 'none'
                      }}>
                        <div className="card-header" style={{ 
                          backgroundColor: COLORS.lightBg,
                          borderBottom: `1px solid ${COLORS.lightBg}`,
                          borderTopLeftRadius: '8px',
                          borderTopRightRadius: '8px',
                          padding: '16px'
                        }}>
                          <h6 className="card-title mb-0" style={{ 
                            color: COLORS.primary,
                            fontWeight: '600'
                          }}>
                            <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                              width: '30px',
                              height: '30px',
                              backgroundColor: '#fff3e0',
                              color: '#f57c00'
                            }}>
                              <i className="bi bi-check-circle"></i>
                            </div>
                            Compléments
                          </h6>
                        </div>
                        <div className="card-body">
                          {tache.commentaire_final && (
                            <div className="mb-3">
                              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                                Commentaire final
                              </label>
                              <div className="border rounded p-3" style={{ 
                                borderColor: '#e0e0e0',
                                backgroundColor: '#F8F9FA',
                                color: COLORS.darkText
                              }}>
                                {tache.commentaire_final}
                              </div>
                            </div>
                          )}
                          
                          {tache.preuve_path && (
                            <div className="mb-3">
                              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                                Preuve
                              </label>
                              <div className="border rounded p-3" style={{ 
                                borderColor: '#e0e0e0',
                                backgroundColor: '#F8F9FA',
                                color: COLORS.darkText
                              }}>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-paperclip me-2"></i>
                                  <a href={`${API_BASE_URL}/${tache.preuve_path}`} target="_blank" rel="noopener noreferrer">
                                    Voir la pièce jointe
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {tache.date_execution_tache && (
                            <div className="mb-3">
                              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                                Date d'exécution
                              </label>
                              <p style={{ color: COLORS.darkText }}>
                                {formatDate(tache.date_execution_tache)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Colonne droite */}
                  <div className="col-lg-6">
                    {/* Agent */}
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-header" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `1px solid ${COLORS.lightBg}`,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        padding: '16px'
                      }}>
                        <h6 className="card-title mb-0" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-person-badge"></i>
                          </div>
                          Agent assigné
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <div className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                              style={{ 
                                width: '60px',
                                height: '60px',
                                fontSize: '20px',
                                backgroundColor: COLORS.primary,
                                color: 'white',
                                boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                              }}>
                              {getAgentInitials()}
                            </div>
                          </div>
                          <div>
                            <h5 className="mb-1" style={{ color: COLORS.darkText }}>
                              {getAgentName()}
                            </h5>
                            <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                              <i className="bi bi-person-badge me-1"></i>
                              Agent de sécurité
                            </p>
                            <p className="mb-2" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                              <i className="bi bi-card-text me-1" style={{ color: '#6c757d' }}></i>
                              Matricule: <span className="fw-semibold">{tache.agent_info?.matricule || '-'}</span>
                            </p>
                            {tache.agent_info?.email && (
                              <p className="mb-0" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                                <i className="bi bi-envelope me-1" style={{ color: '#6c757d' }}></i>
                                {tache.agent_info.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Informations système */}
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-header" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `1px solid ${COLORS.lightBg}`,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        padding: '16px'
                      }}>
                        <h6 className="card-title mb-0" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-calendar"></i>
                          </div>
                          Dates importantes
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
                              Créée le
                            </label>
                            <p style={{ color: COLORS.darkText }}>
                              {formatDate(tache.created_at)}
                            </p>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                              <i className="bi bi-arrow-clockwise me-1" style={{ color: COLORS.secondary }}></i>
                              Dernière modification
                            </label>
                            <p style={{ color: COLORS.darkText }}>
                              {formatDate(tache.updated_at)}
                            </p>
                          </div>
                          
                          {tache.date_execution_tache && (
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                                <i className="bi bi-check-circle me-1" style={{ color: COLORS.secondary }}></i>
                                Exécutée le
                              </label>
                              <p style={{ color: COLORS.darkText }}>
                                {formatDate(tache.date_execution_tache)}
                              </p>
                            </div>
                          )}
                          
                          {duration && (
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                                <i className="bi bi-clock me-1" style={{ color: COLORS.secondary }}></i>
                                Durée prévue
                              </label>
                              <p style={{ color: COLORS.darkText }}>
                                {duration}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section d'état de la tâche (simplifiée) */}
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-header" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `1px solid ${COLORS.lightBg}`,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        padding: '16px'
                      }}>
                        <h6 className="card-title mb-0" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-info-square"></i>
                          </div>
                          Aperçu de l'état
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-2" style={{ 
                                width: '10px',
                                height: '10px',
                                backgroundColor: tache.statut_tache === 'en_attente' || tache.statut_tache === 'en attente' ? COLORS.warning : '#e0e0e0'
                              }}></div>
                              <span className={tache.statut_tache === 'en_attente' || tache.statut_tache === 'en attente' ? 'fw-semibold' : 'text-muted'}>
                                En attente
                              </span>
                            </div>
                          </div>
                          
                          <div className="col-md-6 mb-2">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-2" style={{ 
                                width: '10px',
                                height: '10px',
                                backgroundColor: tache.statut_tache === 'en_cours' || tache.statut_tache === 'en cours' ? COLORS.info : '#e0e0e0'
                              }}></div>
                              <span className={tache.statut_tache === 'en_cours' || tache.statut_tache === 'en cours' ? 'fw-semibold' : 'text-muted'}>
                                En cours
                              </span>
                            </div>
                          </div>
                          
                          <div className="col-md-6 mb-2">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-2" style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: tache.statut_tache === 'en_validation' ? COLORS.warning : '#e0e0e0'
                              }}></div>
                              <span className={tache.statut_tache === 'en_validation' ? 'fw-semibold' : 'text-muted'}>
                                En attente de validation
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6 mb-2">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-2" style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: tache.statut_tache === 'terminee' ? COLORS.success : '#e0e0e0'
                              }}></div>
                              <span className={tache.statut_tache === 'terminee' ? 'fw-semibold' : 'text-muted'}>
                                Terminée
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6 mb-2">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-2" style={{ 
                                width: '10px',
                                height: '10px',
                                backgroundColor: isTaskLate ? COLORS.danger : COLORS.success
                              }}></div>
                              <span className={isTaskLate ? 'fw-semibold text-danger' : 'fw-semibold text-success'}>
                                {isTaskLate ? 'En retard' : 'Dans les temps'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Validation gestionnaire : uniquement quand la tâche attend une validation */}
                {userInfo?.profile === 3 && tache.statut_tache === 'en_validation' && (
                  <div className="d-flex justify-content-end gap-2 mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                    <button
                      className="btn fw-semibold"
                      onClick={() => handleChangeStatutTache('en_cours')}
                      title="Renvoyer la tâche en cours"
                      style={{
                        color: COLORS.warning,
                        borderColor: COLORS.warning,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 20px'
                      }}
                    >
                      <i className="bi bi-arrow-counterclockwise me-2"></i> Renvoyer en cours
                    </button>
                    <button
                      className="btn fw-semibold"
                      onClick={() => handleChangeStatutTache('terminee')}
                      title="Valider et clôturer la tâche"
                      style={{
                        color: 'white',
                        backgroundColor: COLORS.success,
                        borderColor: COLORS.success,
                        borderRadius: '8px',
                        padding: '10px 20px'
                      }}
                    >
                      <i className="bi bi-check-circle me-2"></i> Valider (clôturer)
                    </button>
                  </div>
                )}

                {/* Boutons Modifier et Supprimer en bas sous forme d'icônes */}
                {userInfo?.profile === 3 && (
                  <div className="d-flex justify-content-end gap-2 mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                    {/* Bouton Modifier */}
                    <button
                      className="btn fw-semibold"
                      onClick={() => navigate(`/taches/edit/${tache.id_tache}`)}
                      title="Modifier la tâche"
                      style={{
                        color: COLORS.info,
                        borderColor: COLORS.info,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.info;
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.info;
                      }}
                    >
                      <i className="bi bi-pencil me-2"></i> Modifier
                    </button>
                    
                    {/* Bouton Supprimer */}
                    <button
                      className="btn fw-semibold"
                      onClick={handleDeleteTache}
                      title="Supprimer la tâche"
                      style={{
                        color: COLORS.danger,
                        borderColor: COLORS.danger,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.danger;
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.danger;
                      }}
                    >
                      <i className="bi bi-trash me-2"></i> Supprimer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
};

export default TacheDetail;